import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, Keyboard } from 'swiper/modules';
import { Swiper as SwiperType } from 'swiper';
import GoogleMap from '../components/GoogleMap';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
interface Resort {
  _id: string;
  name: string;
  bannerImage: string;
  destination: {
    _id: string;
    name: string;
  };
  about: string;
  amenities: Array<string | { name: string; icon?: string }>;
  gallery: string[];
  testimonials: Array<{
    customerName: string;
    rating: number;
    comment: string;
    date: string;
  }>;
  averageRating: number;
  isPartnerHotel: boolean;
  resortCode: string;
  isActive: boolean;
  location?: {
    address?: string;
    coordinates?: [number, number] | { latitude: number; longitude: number };
  };
  contactInfo?: {
    phone?: string;
    email?: string;
    website?: string;
  };
}

interface Package {
  _id: string;
  name: string;
  priceUSD: number;
  priceAUD: number;
  validityPeriod: number;
  maxGuests: number;
  resorts: string[];
  isActive?: boolean;
  active?: boolean;
  isLive?: boolean;
}

// Normalize image URLs to work in dev (3000->5000) and prod
const resolveImageUrl = (img?: string) => {
  if (!img) return '';
  const normalized = String(img).replace(/\\/g, '/');
  // If absolute URL or data URL, return as-is
  if (/^(https?:)?\/\//i.test(normalized) || normalized.startsWith('data:')) {
    return normalized;
  }
  const idx = normalized.indexOf('/uploads/');
  const relPath = idx !== -1 ? normalized.slice(idx) : (normalized.startsWith('/') ? normalized : `/${normalized}`);
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const base = isDev ? 'http://localhost:5000' : '';
  return `${base}${relPath}`;
};

const ResortDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { currency, formatPrice } = useCurrency();
  const [resort, setResort] = useState<Resort | null>(null);
  const [packages, setPackages] = useState<Package[]>([]);
  const [similarResorts, setSimilarResorts] = useState<Resort[]>([]);
  const [loading, setLoading] = useState(true);
  // Removed unused selectedImage and activeTab to fix ESLint warnings
  // const [selectedImage, setSelectedImage] = useState('');
  // const [activeTab, setActiveTab] = useState<'overview' | 'amenities' | 'gallery' | 'location' | 'reviews'>('overview');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const lightboxSwiperRef = useRef<SwiperType | null>(null);
  const [currentSection, setCurrentSection] = useState(0);

  // Navigate to package details page to show all resort options
  const handleBookNow = (pkg: Package) => {
    // Go to package details page where user can see all resort options
    navigate(`/packages/${pkg._id}`);
  };

  // Handle hero book now button - shows packages section or goes to single package
  const handleHeroBookNow = () => {
    if (packages.length === 1) {
      // If only one package, go directly to it
      navigate(`/packages/${packages[0]._id}`);
    } else if (packages.length > 1) {
      // If multiple packages, scroll to packages section
      const packagesSection = document.querySelector('[data-section="packages"]');
      packagesSection?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const fetchResortData = async () => {
      if (!slug) return;

      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const isObjectId = /^[0-9a-fA-F]{24}$/.test(slug);
        const endpoint = isObjectId
          ? `${API_BASE}/api/resorts/${slug}`
          : `${API_BASE}/api/resorts/slug/${slug}`;

        const resortResponse = await fetch(endpoint);
        if (resortResponse.ok) {
          const resortJson = await resortResponse.json();
          const resObj = resortJson.resort || resortJson;
          setResort(resObj);
          // setSelectedImage(resolveImageUrl(resObj.bannerImage)); // Removed unused variable

          // Fetch packages and filter by this resort ID
          const packagesResponse = await fetch(`${API_BASE}/api/packages`);
          if (packagesResponse.ok) {
            const packagesJson = await packagesResponse.json();
            const allPackages = packagesJson.packages || packagesJson;
            console.log('🔍 All packages:', allPackages);
            console.log('🏨 Current resort ID:', resObj._id);
            console.log('🏨 Current resort name:', resObj.name);
            console.log('🏨 Current resort object:', resObj);
            
            const relatedPackages = (Array.isArray(allPackages) ? allPackages : []).filter((pkg: Package) => {
              const active = (pkg.isActive ?? pkg.active) !== false;
              const live = pkg.isLive !== false; // More lenient - if isLive is undefined, allow it
              const hasResorts = Array.isArray(pkg.resorts) && pkg.resorts.length > 0;
              const includesThisResort = hasResorts && pkg.resorts.some((resortRef: any) => {
                // Handle different formats: string ID, object with _id, or ObjectId
                let resortId: string;
                if (typeof resortRef === 'string') {
                  resortId = resortRef;
                } else if (resortRef && typeof resortRef === 'object') {
                  resortId = resortRef._id || resortRef.toString();
                } else {
                  resortId = String(resortRef);
                }
                
                const currentId = String(resObj._id);
                const matches = resortId === currentId;
                
                console.log(`🔍 Comparing resort IDs:`, {
                  resortRef,
                  resortId,
                  currentId,
                  matches
                });
                
                return matches;
              });
              
              console.log(`📦 Package ${pkg.name}:`, {
                active,
                live,
                isLive: pkg.isLive,
                hasResorts,
                resorts: pkg.resorts,
                currentResortId: resObj._id,
                includesThisResort
              });
              
              return active && live && includesThisResort;
            });
            
            console.log('✅ Related packages found:', relatedPackages);
            setPackages(relatedPackages);
          }

          // Fetch similar resorts (from same destination, excluding current resort)
          if (resObj.destination) {
            const resortsResponse = await fetch(`${API_BASE}/api/resorts`);
            if (resortsResponse.ok) {
              const resortsJson = await resortsResponse.json();
              const allResorts = resortsJson.resorts || resortsJson;
              const similar = (Array.isArray(allResorts) ? allResorts : []).filter((r: Resort) => {
                const isActive = r.isActive !== false;
                const sameDestination = r.destination === resObj.destination || 
                  (typeof r.destination === 'object' && typeof resObj.destination === 'object' && 
                   r.destination?._id === resObj.destination?._id);
                const differentResort = r._id !== resObj._id;
                return isActive && sameDestination && differentResort;
              }).slice(0, 4); // Limit to 4 similar resorts
              setSimilarResorts(similar);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching resort data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResortData();
  }, [slug]);

  // Lock body scroll when lightbox is open and handle ESC to close
  useEffect(() => {
    if (lightboxOpen) {
      const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightboxOpen(false); };
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', onKey);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', onKey);
      };
    }
  }, [lightboxOpen]);

  // Scroll spy to track current section
  useEffect(() => {
    const sections = document.querySelectorAll('[data-section]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionIndex = parseInt(entry.target.getAttribute('data-section') || '0');
            setCurrentSection(sectionIndex);
          }
        });
      },
      { threshold: 0.5, rootMargin: '-20% 0px -20% 0px' }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [resort]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gradient-to-r from-yellow-600 to-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading resort details...</p>
        </div>
      </div>
    );
  }

  if (!resort) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Resort Not Found</h1>
          <Link to="/destinations" className="text-orange-600 hover:text-orange-800">
            ← Back to Destinations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{resort.name} - Resort Details | Bali Getaway</title>
        <meta name="description" content={`Experience luxury at ${resort.name} in ${resort.destination.name}. Premium amenities and exceptional service.`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        {/* Split Mosaic Hero */}
        <div className="relative">
          {(() => {
            const heroImages = [resort.bannerImage, ...(resort.gallery || [])].filter(Boolean);
            return (
              <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[420px] md:h-[520px]">
                {/* Left large tile */}
                {heroImages[0] && (
                  <button
                    onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
                    className="relative col-span-2 row-span-2 overflow-hidden"
                  >
                    <img src={resolveImageUrl(heroImages[0])} alt="" className="w-full h-full object-cover" />
                    <span className="absolute inset-0 bg-gradient-to-t from-black/30 to-black/0" />
                  </button>
                )}
                {/* Right top wide */}
                {heroImages[1] && (
                  <button
                    onClick={() => { setLightboxIndex(1); setLightboxOpen(true); }}
                    className="relative col-span-2 row-span-1 overflow-hidden"
                  >
                    <img src={resolveImageUrl(heroImages[1])} alt="" className="w-full h-full object-cover" />
                    <span className="absolute inset-0 bg-black/10" />
                  </button>
                )}
                {/* Right bottom two */}
                {heroImages[2] && (
                  <button
                    onClick={() => { setLightboxIndex(2); setLightboxOpen(true); }}
                    className="relative col-span-1 row-span-1 overflow-hidden"
                  >
                    <img src={resolveImageUrl(heroImages[2])} alt="" className="w-full h-full object-cover" />
                    <span className="absolute inset-0 bg-black/10" />
                  </button>
                )}
                {heroImages[3] && (
                  <button
                    onClick={() => { setLightboxIndex(3); setLightboxOpen(true); }}
                    className="relative col-span-1 row-span-1 overflow-hidden"
                  >
                    <img src={resolveImageUrl(heroImages[3])} alt="" className="w-full h-full object-cover" />
                    <span className="absolute inset-0 bg-black/10" />
                  </button>
                )}
              </div>
            );
          })()}
          {/* Centered overlay info */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-white max-w-4xl mx-auto px-6">
              <div className="mb-4">
                <img src="/logo.png" alt="Bali Getaway" className="h-14 w-14 mx-auto" />
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold mb-2 bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent drop-shadow-sm">{resort.name}</h1>
              <p className="text-lg md:text-2xl mb-3 text-yellow-100">{resort.destination.name}</p>
              <div className="flex items-center justify-center mb-4">
                <div className="flex text-yellow-300 mr-3">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className={i < Math.floor(resort.averageRating || 4.5) ? 'text-yellow-300' : 'text-yellow-800/40'}>★</span>
                  ))}
                </div>
                <span className="text-white/95 font-medium">{resort.averageRating || 4.5}/5</span>
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
                  className="px-5 py-2 rounded-full bg-gradient-to-r from-yellow-500 to-orange-600 text-white font-medium shadow hover:from-yellow-600 hover:to-orange-700 pointer-events-auto"
                >
                  View Gallery
                </button>
                <button
                  onClick={() => {
                    // Scroll to packages section instead of using removed activeTab
                    const packagesSection = document.querySelector('[data-section="packages"]');
                    packagesSection?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-5 py-2 rounded-full bg-white/90 text-orange-700 font-medium shadow hover:bg-white pointer-events-auto"
                >
                  View Packages
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Breadcrumb */}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
          <Link to="/destinations" className="text-orange-600 hover:text-orange-800 font-medium">
            Destinations
          </Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to={`/destinations`} className="text-orange-600 hover:text-orange-800 font-medium">
            {resort.destination.name}
          </Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-700 font-medium">{resort.name}</span>
        </nav>

        {/* Sticky Scroll Layout */}
        <div className="flex flex-col lg:flex-row min-h-screen">
          {/* Left Panel - Sticky Banner */}
          <div className="lg:w-1/2 lg:sticky lg:top-0 lg:h-screen hidden md:block">
            <div className="h-full relative overflow-hidden">
              {/* Banner Image */}
              <img 
                src={resolveImageUrl(resort.bannerImage)} 
                alt={resort.name}
                className="w-full h-full object-cover"
              />
              
              {/* Modern Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              
              {/* Content Overlay */}
              <div className="absolute inset-0 flex flex-col justify-end p-6 lg:p-8">
                {/* Resort Info */}
                <div className="text-white space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-orange-300 text-sm font-medium">
                      <span>📍</span>
                      <span>{resort.destination.name}</span>
                    </div>
                    <h1 className="text-3xl lg:text-4xl font-bold leading-tight">{resort.name}</h1>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <div className="flex text-yellow-400">
                          {[...Array(5)].map((_, i) => (
                            <span key={i} className={i < Math.floor(resort.averageRating || 4.5) ? 'text-yellow-400' : 'text-gray-600'}>★</span>
                          ))}
                        </div>
                        <span className="text-white/90 text-sm ml-1">{resort.averageRating || 4.5}</span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        resort.isPartnerHotel 
                          ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                          : 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                      }`}>
                        {resort.isPartnerHotel ? 'Partner Resort' : 'Premium Resort'}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3 pt-2">
                    <button
                      onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
                      className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur border border-white/20 rounded-lg text-white hover:bg-white/20 transition"
                    >
                      <span>🖼️</span>
                      <span className="text-sm font-medium">View Gallery</span>
                    </button>
                    <button 
                      onClick={handleHeroBookNow}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-lg text-white hover:from-yellow-600 hover:to-orange-700 transition shadow-lg"
                      disabled={packages.length === 0}
                    >
                      <span>🏝️</span>
                      <span className="text-sm font-medium">
                        {packages.length === 0 ? 'No Packages' : packages.length === 1 ? 'Book Now' : 'View Packages'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Scrolling Content */}
          <div className="lg:w-1/2 bg-gradient-to-br from-yellow-50 to-orange-50">
            {/* Section Indicators */}
            <div className="sticky top-4 z-10 flex justify-center mb-8 lg:mb-0">
              <div className="flex space-x-2 bg-white/80 backdrop-blur rounded-full px-4 py-2 shadow-sm">
                {['About', 'Amenities', 'Gallery', 'Packages', 'Reviews'].map((label, idx) => (
                  <div
                    key={idx}
                    className={`w-2 h-2 rounded-full transition ${
                      currentSection === idx ? 'bg-orange-500' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Scrolling Sections */}
            <div className="space-y-8">
              {/* Section 0: About */}
              <section data-section="0" className="min-h-[40vh] md:min-h-[50vh] flex flex-col justify-center p-4 md:p-6 lg:p-6">
                <div className="max-w-xl">
                  <div className="mb-8">
                    <span className="inline-block px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-sm font-medium mb-4">About Resort</span>
                    <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">Discover {resort.name}</h2>
                    <div className="prose prose-lg text-gray-700 bg-white/90 backdrop-blur rounded-2xl p-8 border border-orange-200/50 shadow-lg">
                      <p className="leading-relaxed text-gray-600 mb-0">{resort.about}</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 1: Amenities */}
              <section data-section="1" className="min-h-[40vh] md:min-h-[50vh] flex flex-col justify-center p-4 md:p-6 lg:p-6">
                <div className="max-w-2xl">
                  <span className="inline-block px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-sm font-medium mb-4">Resort Features</span>
                  <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">Amenities & Services</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                    {resort.amenities?.map((amenity, index) => {
                      const label = typeof amenity === 'string' ? amenity : amenity.name;
                      const icon = typeof amenity === 'string' ? undefined : amenity.icon;
                      const codeMap: Record<string, string> = {
                        pool: '🏊', wifi: '📶', spa: '💆', gym: '🏋️', beach: '🏖️', dining: '🍽️', bar: '🍹', parking: '🅿️', ac: '❄️', tv: '📺', laundry: '🧺', room_service: '🛎️', breakfast: '🥐', shuttle: '🚌', non_smoking: '🚭', kids: '🧒', pet_friendly: '🐶'
                      };
                      const looksLikeImage = !!icon && (/^https?:\/\//i.test(icon) || icon.includes('/') || icon.includes('.') || icon.startsWith('data:'));
                      const visual = icon ? (looksLikeImage ? null : (codeMap[(icon || '').toLowerCase()] || '✓')) : '✓';
                      return (
                        <div key={index} className="group bg-white/90 backdrop-blur rounded-2xl border border-orange-200/50 shadow-lg hover:shadow-xl transition-all duration-300 p-6 text-center hover:-translate-y-1">
                          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-yellow-100 to-orange-100 border border-orange-200/50 flex items-center justify-center">
                            {icon && looksLikeImage ? (
                              <img src={resolveImageUrl(icon)} alt="" className="w-8 h-8 object-contain" />
                            ) : (
                              <span className="text-orange-500 text-2xl">{visual}</span>
                            )}
                          </div>
                          <div className="text-sm font-semibold text-gray-800 leading-tight">{label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>

              {/* Section 2: Gallery */}
              <section data-section="2" className="min-h-[40vh] md:min-h-[50vh] flex flex-col justify-center p-4 md:p-6 lg:p-6">
                <div className="max-w-2xl">
                  <span className="inline-block px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-sm font-medium mb-4">Visual Experience</span>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-4xl font-bold text-gray-900 leading-tight">Resort Gallery</h2>
                    <button
                      onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-lg hover:from-yellow-600 hover:to-orange-700 transition shadow-lg"
                    >
                      <span>🔍</span>
                      <span className="text-sm font-medium">View All</span>
                    </button>
                  </div>
                  {(() => {
                    const images = [resort.bannerImage, ...(resort.gallery || [])].filter(Boolean);
                    if (!images.length) return <div className="text-gray-600">No images available.</div>;
                    return (
                      <div className="grid grid-cols-2 gap-4">
                        {images.slice(0, 6).map((src, idx) => (
                          <button
                            key={idx}
                            onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
                            className="group relative aspect-[4/3] rounded-2xl overflow-hidden bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                          >
                            <img 
                              src={resolveImageUrl(src)} 
                              alt={`${resort.name} - ${idx + 1}`} 
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                                <span className="text-white text-xl">🔍</span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </section>

              {/* Section 3: Packages */}
              <section data-section="packages" className="min-h-[40vh] md:min-h-[50vh] flex flex-col justify-center p-4 md:p-6 lg:p-6">
                <div className="max-w-2xl">
                  <span className="inline-block px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-sm font-medium mb-4">Resort Packages</span>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-4xl font-bold text-gray-900 leading-tight">Packages Featuring This Resort</h2>
                    <Link to="/packages" className="text-sm text-orange-700 hover:text-orange-900 font-medium">View All →</Link>
                  </div>
                  <div className="space-y-6">
                    {packages.map((pkg) => (
                      <div key={pkg._id} className="bg-white/90 backdrop-blur rounded-2xl border border-orange-200/50 shadow-lg hover:shadow-xl transition-all duration-300 p-8 hover:-translate-y-1">
                        <div className="flex justify-between items-start mb-6">
                          <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                            <p className="text-orange-600 font-medium mb-3 flex items-center gap-2">
                              <span>🏨</span>
                              Choose 1 from {Array.isArray(pkg.resorts) ? pkg.resorts.length : 0} resort{Array.isArray(pkg.resorts) && pkg.resorts.length !== 1 ? 's' : ''} • This resort is an option
                            </p>
                            {Array.isArray(pkg.resorts) && pkg.resorts.length > 1 && (
                              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-3 mb-4">
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0 w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
                                    <span className="text-amber-600 text-sm">ℹ️</span>
                                  </div>
                                  <div className="text-sm text-amber-800">
                                    <p className="font-medium mb-1">Resort Selection Process</p>
                                    <p className="leading-relaxed">
                                      You'll choose <strong>1 resort</strong> from {pkg.resorts.length} available options based on your preference and availability during your travel dates.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <span>👥</span>
                                Up to {pkg.maxGuests} guests
                              </span>
                              <span className="flex items-center gap-1">
                                <span>📅</span>
                                {pkg.validityPeriod}m validity
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-3xl font-extrabold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">{formatPrice(pkg.priceUSD, pkg.priceAUD)}</span>
                            <div className="text-sm text-gray-500">{currency}</div>
                          </div>
                        </div>
                          <div className="flex gap-3">
                          <Link to={`/packages/${pkg._id}`} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 font-medium hover:from-gray-200 hover:to-gray-300 transition-all">
                            <span>📋</span>
                            <span>View All Options</span>
                          </Link>
                          <button
                            onClick={() => handleBookNow(pkg)}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-600 text-white font-semibold hover:from-yellow-600 hover:to-orange-700 shadow-lg hover:shadow-xl transition-all"
                          >
                            <span>🏝️</span>
                            <span>Book Package</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Section 4: Similar Resorts */}
              {similarResorts.length > 0 && (
                <section data-section="similar" className="min-h-[40vh] md:min-h-[50vh] flex flex-col justify-center p-4 md:p-6 lg:p-6">
                  <div className="max-w-2xl">
                    <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-4">Explore More</span>
                    <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">Similar Resorts</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {similarResorts.map((similarResort) => (
                        <Link
                          key={similarResort._id}
                          to={`/resorts/${similarResort._id}`}
                          className="group bg-white/90 backdrop-blur rounded-2xl border border-blue-200/50 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden hover:-translate-y-1"
                        >
                          <div className="relative h-48">
                            <img
                              src={resolveImageUrl(similarResort.bannerImage)}
                              alt={similarResort.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
                              }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                            <div className="absolute bottom-4 left-4 right-4">
                              <h3 className="text-white font-bold text-lg mb-1">{similarResort.name}</h3>
                              {typeof similarResort.destination === 'string' ? (
                                <p className="text-white/90 text-sm">📍 {similarResort.destination}</p>
                              ) : (
                                <p className="text-white/90 text-sm">📍 {similarResort.destination?.name}</p>
                              )}
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                              {similarResort.about || 'Discover this beautiful resort experience.'}
                            </p>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <span>🏨</span>
                                <span>Resort</span>
                              </div>
                              <div className="flex items-center gap-1 text-orange-600 font-medium text-sm">
                                <span>View Details</span>
                                <span>→</span>
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {/* Section 5: Reviews */}
              {resort.testimonials?.length > 0 && (
                <section data-section="5" className="min-h-[40vh] md:min-h-[50vh] flex flex-col justify-center p-4 md:p-6 lg:p-6">
                  <div className="max-w-2xl">
                    <span className="inline-block px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-sm font-medium mb-4">Guest Experience</span>
                    <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">What Our Guests Say</h2>
                    
                    {/* Reviews Carousel */}
                    <div className="relative">
                      <Swiper
                        modules={[Navigation, Pagination, Autoplay]}
                        slidesPerView={1}
                        spaceBetween={0}
                        autoplay={{
                          delay: 4000,
                          disableOnInteraction: false,
                          pauseOnMouseEnter: true
                        }}
                        pagination={{
                          clickable: true,
                          bulletClass: 'swiper-pagination-bullet !bg-orange-300 !opacity-50',
                          bulletActiveClass: 'swiper-pagination-bullet-active !bg-orange-500 !opacity-100'
                        }}
                        navigation={{
                          nextEl: '.review-next',
                          prevEl: '.review-prev'
                        }}
                        loop={resort.testimonials.length > 1}
                        className="reviews-swiper"
                      >
                        {resort.testimonials.map((testimonial, index) => (
                          <SwiperSlide key={index}>
                            <div className="bg-white/90 backdrop-blur rounded-2xl border border-orange-200/50 shadow-lg p-8 mx-1">
                              {/* Quote Icon */}
                              <div className="flex justify-center mb-6">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-100 to-orange-100 flex items-center justify-center">
                                  <span className="text-orange-500 text-2xl">"</span>
                                </div>
                              </div>
                              
                              {/* Review Content */}
                              <div className="text-center">
                                <p className="text-gray-700 text-xl leading-relaxed mb-8 italic font-medium">
                                  "{testimonial.comment}"
                                </p>
                                
                                {/* Rating */}
                                <div className="flex justify-center items-center mb-6">
                                  <div className="flex text-yellow-400 text-2xl">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                      <span key={i}>★</span>
                                    ))}
                                  </div>
                                </div>
                                
                                {/* Author */}
                                <div className="space-y-1">
                                  <div className="font-bold text-gray-900 text-lg">{testimonial.customerName}</div>
                                  <div className="text-gray-500 text-sm">{new Date(testimonial.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                                </div>
                              </div>
                            </div>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                      
                      {/* Navigation Buttons */}
                      {resort.testimonials.length > 1 && (
                        <>
                          <button className="review-prev absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 rounded-full bg-white/90 backdrop-blur border border-orange-200/50 shadow-lg flex items-center justify-center text-orange-600 hover:bg-white transition">
                            <span className="text-lg">‹</span>
                          </button>
                          <button className="review-next absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 rounded-full bg-white/90 backdrop-blur border border-orange-200/50 shadow-lg flex items-center justify-center text-orange-600 hover:bg-white transition">
                            <span className="text-lg">›</span>
                          </button>
                        </>
                      )}
                    </div>
                    
                    {/* Review Counter */}
                    <div className="text-center mt-6">
                      <span className="text-sm text-gray-500">
                        {resort.testimonials.length} guest review{resort.testimonials.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </section>
              )}

              {/* Section 6: Location & Map */}
              {resort.location?.address && (
                <section data-section="location" className="min-h-[40vh] md:min-h-[50vh] flex flex-col justify-center p-4 md:p-6 lg:p-6">
                  <div className="max-w-2xl">
                    <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-4">Location</span>
                    <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">Find Us</h2>
                    
                    {/* Location Info */}
                    <div className="bg-white/90 backdrop-blur rounded-2xl border border-orange-200/50 shadow-lg p-6 mb-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-200/50 flex items-center justify-center flex-shrink-0">
                          <span className="text-2xl">📍</span>
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 mb-2">Resort Address</h3>
                          <p className="text-gray-700 leading-relaxed">{resort.location.address}</p>
                          {resort.location.coordinates && (
                            <div className="mt-3 flex flex-wrap gap-2 text-sm text-gray-600">
                              <span className="inline-flex items-center gap-1">
                                🌐 Coordinates: 
                                {Array.isArray(resort.location.coordinates) 
                                  ? `${resort.location.coordinates[1]}, ${resort.location.coordinates[0]}`
                                  : `${resort.location.coordinates.latitude}, ${resort.location.coordinates.longitude}`
                                }
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Interactive Map */}
                    {resort.location.coordinates && (
                      <GoogleMap
                        latitude={
                          Array.isArray(resort.location.coordinates)
                            ? resort.location.coordinates[1]
                            : resort.location.coordinates.latitude
                        }
                        longitude={
                          Array.isArray(resort.location.coordinates)
                            ? resort.location.coordinates[0]
                            : resort.location.coordinates.longitude
                        }
                        name={resort.name}
                        address={resort.location.address}
                        height="h-64 md:h-80"
                        zoom={15}
                        className="mb-6"
                      />
                    )}

                    {/* Contact Information */}
                    {resort.contactInfo && (
                      <div className="mt-6 bg-white/90 backdrop-blur rounded-2xl border border-orange-200/50 shadow-lg p-6">
                        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                          📞 Contact Information
                        </h3>
                        <div className="grid gap-3">
                          {resort.contactInfo.phone && (
                            <div className="flex items-center gap-3">
                              <span className="text-blue-600">📱</span>
                              <a href={`tel:${resort.contactInfo.phone}`} className="text-blue-600 hover:text-blue-800 font-medium">
                                {resort.contactInfo.phone}
                              </a>
                            </div>
                          )}
                          {resort.contactInfo.email && (
                            <div className="flex items-center gap-3">
                              <span className="text-blue-600">✉️</span>
                              <a href={`mailto:${resort.contactInfo.email}`} className="text-blue-600 hover:text-blue-800 font-medium">
                                {resort.contactInfo.email}
                              </a>
                            </div>
                          )}
                          {resort.contactInfo.website && (
                            <div className="flex items-center gap-3">
                              <span className="text-blue-600">🌐</span>
                              <a href={resort.contactInfo.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-medium">
                                Visit Website
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Overlay */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={(e) => { if (e.currentTarget === e.target) setLightboxOpen(false); }}
        >
          <div className="relative w-full max-w-6xl">
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute -top-10 right-0 text-white/80 hover:text-white text-lg"
            >
              Close ×
            </button>
            <div className="absolute -top-10 left-0 text-white/80 text-sm">
              {lightboxIndex + 1} / {[resort.bannerImage, ...(resort.gallery || [])].length}
            </div>
            <Swiper
              modules={[Navigation, Pagination, Keyboard]}
              navigation
              pagination={{ clickable: true }}
              keyboard={{ enabled: true }}
              initialSlide={lightboxIndex}
              onSwiper={(sw: SwiperType) => { lightboxSwiperRef.current = sw; }}
              onSlideChange={(sw: SwiperType) => setLightboxIndex(sw.activeIndex)}
              className="lightbox-swiper"
            >
              {[resort.bannerImage, ...(resort.gallery || [])].map((src, idx) => (
                <SwiperSlide key={idx}>
                  <img
                    src={resolveImageUrl(src)}
                    alt={`${resort.name} - ${idx + 1}`}
                    className="w-full max-h-[80vh] object-contain"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            {/* Thumbnails */}
            <div className="mt-4 grid grid-cols-5 md:grid-cols-8 gap-2">
              {[resort.bannerImage, ...(resort.gallery || [])].map((src, idx) => (
                <button
                  key={idx}
                  className={`h-14 border ${lightboxIndex === idx ? 'border-orange-400' : 'border-white/20'} rounded overflow-hidden opacity-80 hover:opacity-100`}
                  onClick={() => lightboxSwiperRef.current?.slideTo(idx)}
                >
                  <img src={resolveImageUrl(src)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ResortDetailPage;
